import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import SearchFilterUtil from 'lib/SearchFilterUtil';
import CreatableSelect from 'components/commons/CreatableSelect';
import AreaUnit from 'components/commons/AreaUnit';
import {useResetErrorsOnClear} from 'hooks/useResetErrors';

function RangeAutocompleteWithUnit({
    minOptions,
    maxOptions,
    minValue,
    maxValue,
    unitValue,
    onChange,
    formatter,
    validateInput,
    onError,
    labelMin = 'Min',
    labelMax = 'Max',
    sx,
}) {
    const [minError, setMinError] = useState('');
    const [maxError, setMaxError] = useState('');
    const [areaUnit, setAreaUnit] = useState('');

    // Initial validation on mount
    // This ensures that if the component is rendered with initial values,
    useEffect(() => {
        if (!validateInput) return;
        const minErr = minValue?.value ? validateInput(minValue.value) : '';
        const maxErr = maxValue?.value ? validateInput(maxValue.value) : '';
        setMinError(prev => prev !== minErr ? minErr : prev);
        setMaxError(prev => prev !== maxErr ? maxErr : prev);
        onError?.([minErr, maxErr].filter(Boolean));
    }, []);

    useResetErrorsOnClear(validateInput, minError, maxError, minValue, maxValue, setMinError, setMaxError);

    const validateField = (key, value, otherKey, otherValue, setError, otherError) => {
        const _otherValue = typeof otherValue === 'string' ? otherValue : otherValue?.value;
        const _value = typeof value === 'string' ? value : value?.value;
        if (validateInput) {
            let error = '';
            const isValueGreaterThanOtherValue = (val, otherVal) => {
                try {
                    if (val === undefined || otherVal === undefined) {
                        return true;
                    }
                    if (val === '' || otherVal === '') {
                        return false;
                    }
                    return Number(val) > Number(otherVal);
                } catch (e) {
                    return false;
                }
            };

            // Check comparison error first when one value is undefined
            if (key === 'max') {
                if (isValueGreaterThanOtherValue(_otherValue, _value)) {
                    error = '*Please input a max value greater than min value.';
                }
            } else {
                if (isValueGreaterThanOtherValue(_value, _otherValue)) {
                    error = '*Please input a max value greater than min value.';
                }
            }

            // Only validate input format if no comparison error and value is not empty
            if (!error && _value !== '' && _value !== undefined) {
                error = validateInput(_value);
            }

            setError(error || '');
            if (onError) {
                const errors = key === 'min'
                    ? [error, otherError]
                    : [otherError, error];
                onError(errors.filter(e => Boolean(e)));
            }
        }
    };

    const createHandleChange = (key, setError, otherKey, otherError) => (onChange, otherValue, unitValue) => (_, value) => {
        validateField(key, value, otherKey, otherValue, setError, otherError);

        let _value = value;
        if (typeof value === 'string') {
            _value = {
                label: value,
                value,
            };
        }

        onChange({ [key]: _value, [otherKey]: otherValue, unit: areaUnit || unitValue });
        if (areaUnit) {
            setAreaUnit(null);
        }
    };

    const handleMinChange = createHandleChange('min', setMinError, 'max', maxError);
    const handleMaxChange = createHandleChange('max', setMaxError, 'min', minError);
    const handleUnit = (onChange, minValue, maxValue) => (event) => {
        onChange({ min: minValue, max: maxValue, unit: event.target.value });
    };

    return (
        <>
            <Box sx={{display: 'flex', gap: 1}}>
                <CreatableSelect
                    options={minOptions}
                    value={SearchFilterUtil.getDisplayValue(minOptions, minValue, formatter, minError)}
                    onChange={handleMinChange(onChange, maxValue, unitValue)}
                    label={labelMin}
                    error={!!minError}
                    helperText={minError}
                    filterOptions={(options, params) => SearchFilterUtil.filterOptions(options, params, formatter)}
                    getOptionDisabled={SearchFilterUtil.getMinOptionDisabled(maxValue)}
                    isOptionEqualToValue={SearchFilterUtil.isOptionEqualToValue}
                    selected={minValue}
                    sx={sx || { width: 120 }}
                />
                <CreatableSelect
                    options={maxOptions}
                    value={SearchFilterUtil.getDisplayValue(maxOptions, maxValue, formatter, maxError)}
                    onChange={handleMaxChange(onChange, minValue, unitValue)}
                    label={labelMax}
                    error={!!maxError}
                    helperText={maxError}
                    filterOptions={(options, params) => SearchFilterUtil.filterOptions(options, params, formatter)}
                    getOptionDisabled={SearchFilterUtil.getMaxOptionDisabled(minValue)}
                    isOptionEqualToValue={SearchFilterUtil.isOptionEqualToValue}
                    selected={maxValue}
                    sx={sx || { width: 120 }}
                />
                <AreaUnit
                    value={areaUnit || unitValue}
                    onChange={handleUnit(onChange, minValue, maxValue)}
                />
            </Box>
            {
                (minError || maxError) &&
                <Typography variant="textError2">{ minError || maxError }</Typography>
            }
        </>
    );
}

RangeAutocompleteWithUnit.propTypes = {
    minOptions: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            label: PropTypes.string.isRequired,
        })
    ).isRequired,
    maxOptions: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            label: PropTypes.string.isRequired,
        })
    ).isRequired,
    minValue: PropTypes.object,
    maxValue: PropTypes.object,
    onChange: PropTypes.func.isRequired,
    labelMin: PropTypes.string,
    labelMax: PropTypes.string,
    sx: PropTypes.object,
};

export default RangeAutocompleteWithUnit;